/* eslint-disable */
import { useEffect, useRef } from 'react';
import { Buffer } from 'buffer';
import { getImageFileFromBlob } from '@components/app/DocumentSteps/utils/helpers';
import { ControlHeaderEnum, OODashboardData } from '@features/oneOnboarding/interfaces';
import { CampaignModel } from '@features/oneOnboarding/models/CampaignModel';
import { OODocumentPartModel } from '@features/oneOnboarding/models/DocumentPartModel';
import { UserFlowModel } from '@features/oneOnboarding/models/UserFlow';
import { OOUserFlowModel } from '@features/oneOnboarding/models/UserFlowModel';
import { AppConfig } from '@helpers/appConfig';
import { getBlobData } from '@helpers/fileUpload.helper';
import { Utils } from '@helpers/utils';
import { Onboarding } from '@models/onboarding.model';
import { QuickFilterModel } from '@models/quick-filter.model';
import { SearchResult } from '@models/searchResult.model';
import { CheckUser, UserModel } from '@models/user.model';
import { AdminDashboardFilters } from '@store/slices/oneOnboarding.slice';
import { QueryClient, useMutation, useQueries, useQuery } from '@tanstack/react-query';

import { AamBackendApi } from '../libs/aamBackendApi';
import { SelectedConfiguration } from '@features/selfService/models/configurationModels';
import { UserProfileModel } from '@models/userProfile.model';
import { useDecodedParams } from '@hooks/useDecodedParams';
import { Version } from '@features/oneOnboardingAdmin/components/VersionPicker/VersionPicker';
import { ReportResponse } from '@features/oneOnboarding/models/Report';
import { OOUserDocumentModel } from '@features/oneOnboarding/models/UserDocumentModel';
import { BranchModel } from '@models/branch.model';
import { SiteModel } from '@models/site.model';
import { bulkMailStatus } from '@features/oneOnboardingAdmin/pages/CandidateMailOverview/BulkMailInterfaces';
import { JobTypeModel, RequisitionModel } from '@models/requisition.model';
import { PurchaseOrderModel } from '@models/purchaseOrder.model';
import { ClientModel } from '@models/client.model';

const utils = new Utils();
const appConfig = new AppConfig(utils);
const aamBackendApi = new AamBackendApi(appConfig, utils);

export type getBulkMailPayloadType = {
  page: number;
  resultPerPage: number;
  sort: string;
  sortDir: string;
  status?: bulkMailStatus;
  refetchOnMount: boolean;
  callback?: (data: any) => void;
};

export type SdkCapturedDocumentType = {
  images: string[];
  meta: any[];
};
export type UploadBase64DocumentPayload = {
  tenantId: number;
  userId: string;
  type: string;
  name: string;
  clientOrganizationId: string;
  configurationId: string;
  sdkCapturedDocument: SdkCapturedDocumentType;
  dcDocumentType: string;
  dcAdditionalDocumentType: string;
};

export const useGetUserFlow = (
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  refetchOnMount: boolean = true,
  onSuccess?: (data: UserFlowModel) => void,
) => {
  const query = useQuery<UserFlowModel, Error, UserFlowModel>({
    queryKey: ['getUserFlow', clientOrganizationId, configurationId, userId, applicationId],
    queryFn: () => aamBackendApi.ooGetUserFlow(clientOrganizationId, configurationId, userId, applicationId),
    refetchOnMount,
    enabled: !!clientOrganizationId && !!userId && !!applicationId && !!configurationId,
  });

  const updatedAt = useRef(query.dataUpdatedAt);
  useEffect(() => {
    if (query.data && onSuccess) {
      if (updatedAt.current !== query.dataUpdatedAt) {
        onSuccess(query.data);
      }
    }
  }, [query.dataUpdatedAt]);

  return query;
};

export const useGetOnboardingCheck = (
  clientOrganizationId: string,
  configurationId: string,
  user: CheckUser | undefined,
  version: number,
) => {
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['getOnboardingChecked', clientOrganizationId, configurationId, user?.userId],
    queryFn: () => aamBackendApi.checkCandidate(clientOrganizationId, configurationId, [user!], false, {}, version),
    refetchOnMount: true,
    enabled: !!clientOrganizationId && !!configurationId && !!user?.userId,
  });
};

export const useGetOnboardingsWithRelationsMultiple = (
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  onSuccess?: (data: Onboarding) => void,
) => {
  const query = useQuery({
    queryKey: ['getOnboardingsWithRelationsMultiple', clientOrganizationId, configurationId, applicationId, userId],
    queryFn: () =>
      aamBackendApi.getOnboardingsWithRelationsMultiple(clientOrganizationId, configurationId, applicationId, userId),
    refetchOnMount: true,
    enabled: !!clientOrganizationId && !!userId && !!applicationId && !!configurationId,
  });

  useEffect(() => {
    if (query.data && onSuccess) {
      onSuccess(query.data);
    }
  }, [query.data]);

  return query;
};

export const mutateUserFlow = (
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  queryClient: QueryClient,
  callback?: any,
) =>
  useMutation({
    mutationFn: async (payload: any) => {
      const { data, files } = payload;
      try {
        return await aamBackendApi.updateUserFlow(
          clientOrganizationId,
          configurationId,
          userId,
          data,
          files ?? {},
          applicationId,
        );
      } catch (err) {
        throw err;
      }
    },
    onSuccess: () => {
      callback?.();
      queryClient.invalidateQueries({
        queryKey: ['getUserFlow', clientOrganizationId, configurationId, userId, applicationId],
      });
    },
  });

export const mutateRejectUser = (
  clientOrganizationId: string,
  configurationId: string,
  selectedUserId: string,
  applicationId: string,
  rejectReason: string,
  callbacks: () => void,
) =>
  useMutation({
    mutationFn: () =>
      aamBackendApi.ooRejectUser(clientOrganizationId, configurationId, selectedUserId, applicationId, rejectReason),
    onSuccess: () => {
      callbacks();
    },
  });

export const mutateUnRejectUser = (
  clientOrganizationId: string,
  configurationId: string,
  selectedUserId: string,
  applicationId: string,
  callbacks: () => void,
) =>
  useMutation({
    mutationFn: () =>
      aamBackendApi.ooUnRejectUser(clientOrganizationId, configurationId, selectedUserId, applicationId),
    onSuccess: () => {
      callbacks();
    },
  });

export const mutateCompleteStep = (
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  stepName: string,
  queryClient: QueryClient,
  callbacks?: () => void,
) =>
  useMutation({
    mutationFn: () =>
      aamBackendApi.ooAdminCompleteStepForUser(
        clientOrganizationId,
        configurationId,
        userId,
        { stepName },
        applicationId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getUserFlow', clientOrganizationId, configurationId, userId, applicationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['getOnboardingsWithRelationsMultiple', clientOrganizationId, configurationId, applicationId, userId],
      });
      queryClient.invalidateQueries({
        queryKey: ['getUserOOFlow', userId, applicationId, clientOrganizationId, configurationId],
      });
      if (callbacks) {
        callbacks();
      }
    },
  });

export const useGetUser = (tenantId: number | undefined, userId: string, config?: any) =>
  useQuery<UserModel | undefined>({
    queryKey: ['getUser', userId, tenantId],
    queryFn: () => aamBackendApi.fetchUser(tenantId!, userId),
    enabled: !!tenantId && !!userId,
    ...config,
  });

export const useGetUserProfile = (tenantId: number, userId: string, onSuccess?: (data: any) => void) => {
  const query = useQuery<UserProfileModel>({
    queryKey: ['getUserProfile', userId, tenantId],
    queryFn: () => aamBackendApi.getUserProfile(tenantId, userId),
    refetchOnMount: true,
    enabled: !!tenantId && !!userId,
  });

  useEffect(() => {
    if (query.data && onSuccess) {
      onSuccess(query.data);
    }
  }, [query.data]);

  return query;
};

export const useGetCountryById = (countryId: string) => {
  return useQuery({
    queryKey: ['getCountryById', countryId],
    queryFn: () => aamBackendApi.ooGetCountryById(countryId),
    enabled: !!countryId,
  });
};

export const useGetProvinces = (data: object, clientId: string, confId: string) => {
  return useQuery({
    queryKey: ['getProvinces', data, clientId, confId],
    queryFn: () => aamBackendApi.getControlItems(ControlHeaderEnum.province, data, clientId, confId),
    enabled: !!clientId,
  });
};

export const useGetMuncipalities = (data: object, clientId: string, confId: string) => {
  return useQuery({
    queryKey: ['getMuncipalities', data, clientId, confId],
    queryFn: () => aamBackendApi.getControlItems(ControlHeaderEnum.municipality, data, clientId, confId),
    enabled: !!clientId,
  });
};

export const useGetJourneysByTenant = (tenantId: number) => {
  return useQuery({
    queryKey: ['getJourneysByTenant', tenantId],
    queryFn: () => aamBackendApi.getJourneysByTenant(tenantId),
    enabled: !!tenantId,
  });
};

export const useGetRequisitionsByTenant = (includeInvalid?: boolean) => {
  return useQuery({
    queryKey: ['getRequisitionsByTenant'],
    queryFn: () => aamBackendApi.getRequisitionsByTenant(includeInvalid),
    enabled: true,
  });
};

export const mutatePurchaseOrder = (
  queryClient: QueryClient,
  update: boolean,
  includeInvalid: boolean = false,
  callbacks?: (data: any) => void,
) =>
  useMutation({
    mutationFn: async (payload: { data: PurchaseOrderModel; id?: string }): Promise<PurchaseOrderModel> => {
      try {
        return update && payload.id
          ? await aamBackendApi.updatePurchaseOrder(payload.data, payload.id, includeInvalid)
          : await aamBackendApi.createPurchaseOrder(payload.data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: PurchaseOrderModel) => {
      queryClient.invalidateQueries({ queryKey: ['getPurchaseOrders'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const mutatePurchaseOrderBatch = (queryClient: QueryClient, callbacks?: (data: any) => void) =>
  useMutation({
    mutationFn: async (data: PurchaseOrderModel[]): Promise<PurchaseOrderModel[]> => {
      try {
        return await aamBackendApi.createPurchaseOrderBatch(data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: PurchaseOrderModel[]) => {
      queryClient.invalidateQueries({ queryKey: ['getPurchaseOrders'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const useGetPurchaseOrders = (includeInvalid?: boolean) => {
  return useQuery({
    queryKey: ['getPurchaseOrders'],
    queryFn: () => aamBackendApi.getPurcahseOrders(includeInvalid),
    enabled: true,
  });
};

export const mutateClient = (
  queryClient: QueryClient,
  update: boolean,
  includeInvalid: boolean = false,
  callbacks?: (data: any) => void,
) =>
  useMutation({
    mutationFn: async (payload: { data: ClientModel; id?: string }): Promise<ClientModel> => {
      try {
        return update && payload.id
          ? await aamBackendApi.updateClient(payload.data, payload.id, includeInvalid)
          : await aamBackendApi.createClient(payload.data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: ClientModel) => {
      queryClient.invalidateQueries({ queryKey: ['getClients'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const mutateClientBatch = (queryClient: QueryClient, callbacks?: (data: any) => void) =>
  useMutation({
    mutationFn: async (data: ClientModel[]): Promise<ClientModel[]> => {
      try {
        return await aamBackendApi.createClientBatch(data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: ClientModel[]) => {
      queryClient.invalidateQueries({ queryKey: ['getClients'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const useGetClients = (includeInvalid?: boolean) => {
  return useQuery({
    queryKey: ['getClients'],
    queryFn: () => aamBackendApi.getClients(includeInvalid),
    enabled: true,
  });
};

export const mutateRequisition = (
  queryClient: QueryClient,
  update: boolean,
  includeInvalid: boolean = false,
  callbacks?: (data: any) => void,
) =>
  useMutation({
    mutationFn: async (payload: { data: RequisitionModel; id?: string }): Promise<RequisitionModel> => {
      try {
        return update && payload.id
          ? await aamBackendApi.updateRequisition(payload.data, payload.id, includeInvalid)
          : await aamBackendApi.createRequisition(payload.data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: RequisitionModel) => {
      queryClient.invalidateQueries({ queryKey: ['getRequisitionsByTenant'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const mutateRequisitionBatch = (queryClient: QueryClient, callbacks?: (data: any) => void) =>
  useMutation({
    mutationFn: async (data: RequisitionModel[]): Promise<RequisitionModel[]> => {
      try {
        return await aamBackendApi.createRequisitionBatch(data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: RequisitionModel[]) => {
      queryClient.invalidateQueries({ queryKey: ['getRequisitionsByTenant'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const useGetJobTypes = (includeInvalid: boolean = false) => {
  return useQuery({
    queryKey: ['getJobTypes'],
    queryFn: () => aamBackendApi.getJobTypes(includeInvalid),
  });
};

export const mutateJobType = (
  queryClient: QueryClient,
  update: boolean,
  includeInvalid: boolean = false,
  callbacks?: (data: any) => void,
) =>
  useMutation({
    mutationFn: async (payload: { data: JobTypeModel; id?: string }): Promise<JobTypeModel> => {
      try {
        return update && payload.id
          ? await aamBackendApi.updateJobType(payload.data, payload.id, includeInvalid)
          : await aamBackendApi.createJobType(payload.data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: JobTypeModel) => {
      queryClient.invalidateQueries({ queryKey: ['getJobTypes'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const mutateJobTypeBatch = (queryClient: QueryClient, callbacks?: (data: any) => void) =>
  useMutation({
    mutationFn: async (data: JobTypeModel[]): Promise<JobTypeModel[]> => {
      try {
        return await aamBackendApi.createJobTypeBatch(data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: JobTypeModel[]) => {
      queryClient.invalidateQueries({ queryKey: ['getJobTypes'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const useFetchBranches = (enabled = true, includeInvalid?: boolean) =>
  useQuery({ queryKey: ['getBranches'], queryFn: () => aamBackendApi.fetchBranches(includeInvalid), enabled });

export const mutateBranch = (
  queryClient: QueryClient,
  update: boolean,
  includeInvalid: boolean = false,
  callbacks?: (data: any) => void,
) =>
  useMutation({
    mutationFn: async (payload: { data: BranchModel; id?: string }): Promise<BranchModel> => {
      try {
        return update && payload.id
          ? await aamBackendApi.updateBranch(payload.data, payload.id, includeInvalid)
          : await aamBackendApi.createBranch(payload.data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: BranchModel) => {
      queryClient.invalidateQueries({ queryKey: ['getBranches'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const mutateBranchBatch = (queryClient: QueryClient, callbacks?: (data: any) => void) =>
  useMutation({
    mutationFn: async (data: BranchModel[]): Promise<BranchModel[]> => {
      try {
        return await aamBackendApi.createBranchBatch(data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: BranchModel[]) => {
      queryClient.invalidateQueries({ queryKey: ['getBranches'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const useDownloadDocumentWithFullResponse = (tenantId: number, parts: Array<OODocumentPartModel>) => {
  const queries = parts.map((part: OODocumentPartModel) => {
    const key = [
      'part',
      tenantId,
      `${part.userDocumentPart.userDocumentId}/${part.name}`,
      part.userDocumentPart?.metadata?.filename || '',
    ];

    return {
      queryKey: key,
      queryFn: () =>
        aamBackendApi
          .downloadDocumentWithFullResponse(
            tenantId,
            part.userDocumentPart.userId,
            `${part.userDocumentPart.userDocumentId}/${part.name}`,
          )
          .then((response) => {
            const { data, headers } = response;
            const file = getBlobData(data, headers['content-type']);
            const fileImage = getImageFileFromBlob(data);
            return {
              ...file,
              file: fileImage,
              name: part.name,
            };
          }),
      enabled: !!tenantId && !!part.userDocumentPart.userId,
      refetchOnWindowFocus: true,
    };
  });

  return useQueries({ queries });
};

export const mutateStepData = (
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  queryClient: QueryClient,
) => {
  return useMutation({
    mutationFn: (data: any) =>
      aamBackendApi.updateStepData(clientOrganizationId, configurationId, userId, applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getUserFlow', clientOrganizationId, configurationId, userId, applicationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['getOnboardingsWithRelationsMultiple', clientOrganizationId, configurationId, applicationId, userId],
      });
      queryClient.invalidateQueries({
        queryKey: ['getOnboardingChecked', clientOrganizationId, configurationId, userId],
      });
      queryClient.invalidateQueries({ queryKey: ['getUserProfile', userId] });
    },
  });
};
export const mutateOneOnboarding = (
  tenantId: number,
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  queryClient: QueryClient,
  callbacks?: () => void,
) => {
  return useMutation({
    mutationFn: (data: any) =>
      aamBackendApi.updateOneOnboarding(clientOrganizationId, configurationId, userId, applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['checkUser', tenantId, clientOrganizationId, configurationId, userId],
      });
      if (callbacks) callbacks();
    },
  });
};
export const mutateSignDocument = (
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  lang: string,
) => {
  return useMutation({
    mutationFn: (payload: { document: any; filterName?: string }) =>
      aamBackendApi.getOneSpanOOSignAllDocumentUrl(clientOrganizationId, {
        document: payload.document.name,
        callbackUrl: `${window.location.origin}/oo/recruiter/onespan-callback`,
        returnUrl: window.location.href,
        clientOrganizationId,
        configurationId,
        applicationId,
        additionalReturnParams: {
          candidateId: userId,
          documentId: payload.document.id,
          lang: lang,
          filter: payload?.filterName,
        },
      }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
};

export const useUserOOFlow = (
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  callbacks?: (data: OOUserFlowModel) => void,
) => {
  const query = useQuery({
    queryKey: ['getUserFlow', clientOrganizationId, configurationId, userId, applicationId],
    queryFn: () =>
      aamBackendApi.fetchUserFlowWithCurrentStepInfoAndUpdateRedux(
        userId,
        applicationId,
        clientOrganizationId,
        configurationId,
      ),
    enabled: !!userId && !!applicationId && !!clientOrganizationId && !!configurationId,
  });

  useEffect(() => {
    if (query.data && callbacks) {
      callbacks(query.data);
    }
  }, [query.data]);

  return query;
};

export const mutateDeleteUser = (tenantId: number, userId: string, callbacks?: (data: any) => void) => {
  return useMutation({
    mutationFn: ({ sendDeletionMail }: { sendDeletionMail: boolean }) =>
      aamBackendApi.create(`/api/tenant/${tenantId}/user/${userId}`, { sendDeletionMail }),
    onSuccess: (data) => {
      callbacks?.(data);
    },
  });
};

export const useFetchImportUsers = (
  selectedConfiguration: string | undefined,
  selectedConfigurationId: string | null,
  importClientOrganizationId: string | null,
  updateRefetchInterval: (interval: number) => void,
  refetchInterval: number,
  tableParameters: {
    page: number;
    limit: number;
    startDate?: number | null;
    importStatus?: string;
    order: {
      direction: string;
      field: string;
    };
  },
  onSuccess?: (data: any) => void,
) => {
  const query = useQuery({
    queryKey: ['fetchImports', selectedConfiguration, tableParameters],
    queryFn: () => {
      if (!selectedConfiguration) {
        return {};
      }
      const parse = JSON.parse(selectedConfiguration || '');

      return aamBackendApi
        .fetchImportsWithNoRelation(parse.clientOrganizationId, parse.configurationId, tableParameters)
        .then((response) => {
          if (response.results.length && response.results.find((i) => i.status === 'IN_PROGRESS')) {
            updateRefetchInterval(2000);
          } else {
            updateRefetchInterval(0);
          }

          return response;
        });
    },
    enabled: !!selectedConfigurationId && !!importClientOrganizationId,
    refetchOnMount: true,
    refetchInterval,
  });

  useEffect(() => {
    if (query.data && onSuccess) {
      onSuccess(query.data);
    }
  }, [query.data]);

  return query;
};

export const useFetchReports = (tableParameters: {
  page: number;
  rowsPerPage: number;
  order: {
    direction: string;
    field: string;
  };
  startDate?: string | null;
  endDate?: string | null;
}) => {
  return useQuery<ReportResponse>({
    queryKey: ['fetchReports', tableParameters],
    queryFn: () => {
      return aamBackendApi
        .fetchReports(tableParameters)
        .then((response) => {
          return response;
        })
        .catch((err) => {
          throw err;
        });
    },
    enabled: !!tableParameters,
  });
};

export const useCampaign = (url: string, callbacks?: (data: any) => void) => {
  const query = useQuery({
    queryKey: ['fetchCampaign', url],
    queryFn: () => aamBackendApi.fetchCampaign(url),
    enabled: !!url,
  });

  useEffect(() => {
    if (query.data && callbacks) {
      callbacks(query.data);
    }
  }, [query.data]);

  return query;
};

export const mutateActivateCampaign = (
  tenantId: number,
  campaign: CampaignModel | undefined,
  callbacks?: (data: any) => void,
) =>
  useMutation({
    mutationFn: () => aamBackendApi.updateCampaign(tenantId, campaign),
    onSuccess: (data) => callbacks?.(data),
  });

export const useUploadLimitSize = (clientOrganizationId: string, selectedConfigurationId: string) =>
  useQuery({
    queryKey: ['useUploadLimitSize', clientOrganizationId, selectedConfigurationId],
    queryFn: () => aamBackendApi.fetchUploadLimitSize(clientOrganizationId, selectedConfigurationId),
    enabled: !!clientOrganizationId && !!selectedConfigurationId,
  });

export const useAllApplicationsForUserAndClientOrgUploadLimitSize = (
  userId: string | null,
  callbacks?: (data: any) => void,
) => {
  const query = useQuery({
    queryKey: ['getOnboardingsWithRelations', userId],
    queryFn: () => aamBackendApi.getAllApplicationsForUserAndClientOrg(userId!),
    enabled: !!userId,
  });

  useEffect(() => {
    if (query.data && callbacks) {
      callbacks(query.data);
    }
  }, [query.data]);

  return query;
};

export const useGetOnboardingTheme = (
  organizationId: string | null,
  configurationId: string | null,
  callbacks?: (data: any) => void,
) => {
  const query = useQuery({
    queryKey: ['getOnboardingTheme', organizationId, configurationId],
    queryFn: () =>
      aamBackendApi.getOnboardingTheme(
        organizationId ? organizationId : 'default',
        configurationId ? configurationId : 'default',
      ),
    //enabled: !!organizationId && !!configurationId,
  });

  useEffect(() => {
    if (query.data && callbacks) {
      callbacks(query.data);
    }
  }, [query.data]);

  return query;
};

export const useGetRejectReasons = (clientOrganizationId: string, configurationId: string) =>
  useQuery({
    queryKey: ['getRejectReasons'],
    queryFn: () => aamBackendApi.getRejectReasons(clientOrganizationId, configurationId),
  });

export const useFetchSelectedConfiguration = (
  clientOrganizationId: string,
  configurationId: string,
  callbacks?: (data: SelectedConfiguration) => void,
) => {
  const query = useQuery({
    queryKey: ['fetchSelectedConfiguration', clientOrganizationId, configurationId],
    queryFn: () => aamBackendApi.fetchSelectedConfiguration(clientOrganizationId, configurationId),
    enabled: !!clientOrganizationId && !!configurationId,
  });

  useEffect(() => {
    if (query.data && callbacks) {
      callbacks(query.data);
    }
  }, [query.data]);

  return query;
};

export const useAllConfigurationsForTenant = (tenantId: number) =>
  useQuery({
    queryKey: ['fetchClientOrganizationForTenantIncludingTenantUnrelated', tenantId],
    queryFn: () => aamBackendApi.fetchClientOrganizationForTenantIncludingTenantUnrelated(tenantId),
    enabled: !!tenantId,
  });

export const useAllConfigurationsNamesForTenant = (tenantId: number) =>
  useQuery({
    queryKey: ['fetchClientOrganizationNamesForTenant', tenantId],
    queryFn: () => aamBackendApi.fetchClientOrganizationNamesForTenant(tenantId),
    enabled: !!tenantId,
  });

type UseOnboardingFlowUsersOptions = {
  filters: any;
  page: number;
  order: string;
  orderBy: string;
  quickFilter?: string;
  onboardingCreatedAtInterval?: any;
  onboardingDeletedAtInterval?: any;
  rowsPerPage: number;
  quickFilterItemsAvaliable?: any;
  tenantId: number;
  quickFilterItems?: Array<QuickFilterModel> | null;
  otherFilters: any;
  journeyId: string;
  searchOnboardingEndpointVersion: Version;
};

export const useOnboardingFlowUsers = (
  {
    filters,
    page,
    order,
    orderBy,
    quickFilter,
    onboardingCreatedAtInterval,
    rowsPerPage,
    quickFilterItemsAvaliable,
    tenantId,
    quickFilterItems,
    otherFilters,
    journeyId,
    searchOnboardingEndpointVersion,
  }: UseOnboardingFlowUsersOptions,
  callbacks?: (data: any) => void,
) => {
  const query = useQuery<SearchResult>({
    queryKey: [
      'getOnboardingFlowUsers',
      filters,
      page,
      order,
      orderBy,
      quickFilter,
      onboardingCreatedAtInterval,
      rowsPerPage,
      quickFilterItemsAvaliable,
      tenantId,
      quickFilterItems,
      otherFilters,
      journeyId,
      searchOnboardingEndpointVersion,
    ],
    queryFn: () => {
      const payload = {
        page: page + 1,
        sortDir: order.toUpperCase(),
        sort: orderBy,
        ...otherFilters,
        ...onboardingCreatedAtInterval,
        filter: quickFilter,
        tile: quickFilter,
        isOO: true,
        clientOrganizationId: filters.clientOrganizationId,
        configurationId: journeyId,
        resultsPerPage: rowsPerPage,
        quickFilterItems: (quickFilterItems || []).map(({ key, title, description, ...rest }) => ({ ...rest })),
      };

      if (searchOnboardingEndpointVersion === Version.v3) {
        return aamBackendApi.fetchOnboardingFlowUsersv3(payload);
      }
      return aamBackendApi.fetchOnboardingFlowUsersv2(tenantId, payload);
    },
    enabled: !!quickFilterItems && quickFilterItems?.length !== 0,
  });

  useEffect(() => {
    if (query.data && callbacks) {
      callbacks(query.data);
    }
  }, [query.data]);

  return query;
};

export const useLocationsByRegions = (regionIds: string[]) => {
  return useQuery({
    queryKey: ['locationsByRegions', regionIds],
    queryFn: () => aamBackendApi.getLocationsBySelectedRegions(regionIds),
    enabled: !!regionIds.length,
  });
};

export const useRegions = (enabled = true) => {
  return useQuery({
    queryKey: ['getRegions'],
    queryFn: () => aamBackendApi.fetchRegions(),
    retry: false,
    enabled,
  });
};

export const useSites = (enabled = true) => {
  return useQuery({
    queryKey: ['fetchSites'],
    queryFn: () => aamBackendApi.getSites(),
    retry: false,
    enabled,
  });
};

export const useSitesAll = (enabled = true) => {
  return useQuery({
    queryKey: ['fetchSitesAll'],
    queryFn: () => aamBackendApi.getSitesAll(),
    enabled,
  });
};

export const mutateSite = (
  queryClient: QueryClient,
  update: boolean,
  includeInvalid: boolean = false,
  callbacks?: (data: any) => void,
) =>
  useMutation({
    mutationFn: async (payload: { data: SiteModel; id?: string }): Promise<SiteModel> => {
      try {
        return update && payload.id
          ? await aamBackendApi.updateSite(payload.data, payload.id)
          : await aamBackendApi.createSite(payload.data);
      } catch (error) {
        return error;
      }
    },
    onSuccess: (data: SiteModel) => {
      queryClient.invalidateQueries({ queryKey: ['fetchSitesAll'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const mutateSiteBatch = (queryClient: QueryClient, callbacks?: (data: any) => void) =>
  useMutation({
    mutationFn: async (data: SiteModel[]): Promise<SiteModel[]> => {
      try {
        return await aamBackendApi.createSiteBatch(data);
      } catch (error) {
        Utils.consoleError(error);
        return error;
      }
    },
    onSuccess: (data: SiteModel[]) => {
      queryClient.invalidateQueries({ queryKey: ['fetchSitesAll'] });
      if (callbacks) {
        callbacks(data);
      }
    },
  });

export const useCampaigns = (enabled = true) => {
  return useQuery({
    queryKey: ['fetchCampaigns'],
    queryFn: () => aamBackendApi.fetchCampaigns(),
    retry: false,
    enabled,
  });
};
export const useAllForDashboard = (tenantId: number) =>
  useQuery({
    queryKey: ['getAllForDashboard', tenantId],
    queryFn: () =>
      aamBackendApi
        .getAllForDashboard(tenantId)
        .then((data: any) => JSON.parse(Buffer.from(data, 'base64').toString()) as OODashboardData),
    refetchOnMount: true,
    retry: true,
  });

export const useDownloadLogo = (tenantId: number, name = 'default.png') =>
  useQuery({
    queryKey: ['downloadLogo', tenantId, name],
    queryFn: () => aamBackendApi.downloadLogo(tenantId, name),
    enabled: !!tenantId && !!name, // Only fetch if both tenantId and name are present
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - logos are static assets
    gcTime: 1000 * 60 * 60 * 24 * 7, // 7 days - keep in memory cache
    refetchOnMount: false, // Don't refetch on component mount
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
export const useAllConfigurations = (clientOrganizationId: string) =>
  useQuery({
    queryKey: ['allConfigurations', clientOrganizationId],
    queryFn: () => aamBackendApi.fetchAllConfigurations(clientOrganizationId),
    refetchOnMount: true,
    enabled: !!clientOrganizationId,
  });
export const useAllDraftConfigurations = (clientOrganizationId: string) =>
  useQuery({
    queryKey: ['allDraftConfigurations', clientOrganizationId],
    queryFn: () => aamBackendApi.fetchAllDraftConfigurations(clientOrganizationId),
    refetchOnMount: true,
    enabled: !!clientOrganizationId,
  });
export const useAllDraftConfigurationsByTenantId = (clientOrganizationId: string, tenantId: number) =>
  useQuery({
    queryKey: ['allDraftConfigurations', clientOrganizationId],
    queryFn: () => aamBackendApi.fetchAllDraftConfigurationsByTenantId(clientOrganizationId, tenantId),
    refetchOnMount: true,
  });
export const useFetchAvailableTenantsForRecruiter = (recruiterId?: string | null) =>
  useQuery({
    queryKey: ['fetchAvailableTenantsForRecruiter', recruiterId],
    queryFn: () => aamBackendApi.fetchAvailableTenantsForRecruiter(recruiterId!),
    refetchOnMount: true,
    enabled: !!recruiterId,
  });

export const mutateDraftConfiguration = (
  clientOrganizationId: string,
  draftId: string,
  queryClient: QueryClient,
  callback?: any,
) =>
  useMutation({
    mutationFn: (payload: {
      data: {
        configuration: any;
        clientOrganizationId: string;
        configurationId: string;
        configurationChangeStack: any;
      };
      tenantId: string | null;
    }) =>
      draftId
        ? aamBackendApi.updateDraftConfiguration(clientOrganizationId, draftId, payload)
        : aamBackendApi.createDraftConfiguration(clientOrganizationId, payload),
    onSuccess: () => {
      callback?.();
      queryClient.invalidateQueries({ queryKey: ['allDraftConfigurations', clientOrganizationId] });
    },
  });

export const mutateDleteDraftConfiguration = (queryClient: QueryClient) =>
  useMutation({
    mutationFn: (payload: { draftId: string }) => aamBackendApi.deleteDraftConfiguration(payload.draftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allDraftConfigurations', 'x'] });
    },
  });

export const useHasPdfExport = (clientOrganizationId: string, configurationId: string) => {
  return useQuery({
    queryKey: ['getHasPdfExport'],
    queryFn: () => aamBackendApi.hasPdfExport(clientOrganizationId, configurationId),
    retry: true,
    enabled: !!clientOrganizationId && !!configurationId,
  });
};

export const useQuickFilters = (
  onboardingCreatedAtInterval: any,
  adminDashboardFilters: AdminDashboardFilters,
  page: number,
  order: string,
  orderBy: string,
  quickFilter: any,
  rowsPerPage: number,
  quickFilterItemsAvaliable: boolean,
  quickFilterItems: Array<QuickFilterModel> | null,
  otherFilters: Record<any, any>,
  journeyId: string,
  tenantId: number,
) => {
  return useQuery({
    queryKey: [
      'quickFilters',
      onboardingCreatedAtInterval,
      adminDashboardFilters,
      page,
      order,
      orderBy,
      quickFilter,
      rowsPerPage,
      quickFilterItemsAvaliable,
      (quickFilterItems?.map(({ number, ...rest }) => rest) || []) as Array<QuickFilterModel>,
      otherFilters,
      journeyId,
      tenantId,
    ],
    queryFn: () => {
      const payload = {
        page: page + 1,
        sortDir: order.toUpperCase(),
        sort: orderBy,
        ...otherFilters,
        ...onboardingCreatedAtInterval,
        filter: quickFilter,
        tile: quickFilter,
        isOO: true,
        clientOrganizationId: adminDashboardFilters.clientOrganizationId,
        configurationId: journeyId,
        resultsPerPage: rowsPerPage,
        quickFilterItems: quickFilterItems,
      };

      return aamBackendApi.getQuickFilters(tenantId, payload);
    },
    enabled: !!quickFilterItems && quickFilterItems?.length !== 0,
  });
};

export const useGetUserWithPermissions = (tenantId: number, userId: string, callbacks?: (data: UserModel) => void) => {
  const query = useQuery({
    queryKey: ['getUserWithPermissions', tenantId, userId],
    queryFn: () => aamBackendApi.getUserWithPermissions(tenantId, userId),
    enabled: !!userId && userId !== '0',
    retry: true,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (query.data && callbacks) {
      callbacks(query.data);
    }
  }, [query.data]);

  return query;
};

export const useGetUserFlowHistory = (clientOrganizationId: string, userId: string) =>
  useQuery({
    queryKey: ['getUserFlowHistory', clientOrganizationId, userId],
    queryFn: () => aamBackendApi.getUserFlowHistory(clientOrganizationId, userId),
    enabled: !!userId,
    retry: true,
    refetchOnMount: true,
  });
export const useGetUserMailHistory = (userId: string, clientOrganizationId: string, configurationId: string) =>
  useQuery({
    queryKey: ['fetchUserMailHistory', userId, clientOrganizationId, userId],
    queryFn: () => aamBackendApi.fetchUserMailHistory(userId, clientOrganizationId, configurationId),
    enabled: !!userId,
    retry: true,
    refetchOnMount: true,
  });
export const useGetUserActionHistory = (
  tenantId: number,
  userId: string,
  applicationId: string,
  clientOrganizationId: string,
  configurationId: string,
) =>
  useQuery({
    queryKey: ['getUserActionHistory', tenantId, userId, applicationId, clientOrganizationId, configurationId],
    queryFn: () =>
      aamBackendApi.getUserActionHistory(tenantId, userId, applicationId, clientOrganizationId, configurationId),
    enabled: !!userId && !!applicationId && !!clientOrganizationId && !!configurationId,
    retry: true,
    refetchOnMount: true,
  });

export const useUserCheck = (onboarding: Onboarding | undefined, version: number = 3) => {
  const userCheck: CheckUser | undefined = onboarding
    ? {
        clientOrganizationId: onboarding.clientOrganizationId,
        userId: onboarding.userId,
        tenantId: onboarding.tenantId,
        applicationId: onboarding.applicationId || '',
        configurationId: onboarding.configurationId,
        rejectedAt: onboarding.rejectedAt,
      }
    : undefined;
  const { clientOrganizationId = '', configurationId = '' } = useDecodedParams();

  const { data: onboardingChecked, refetch: refetchOnboardingCheck } = useGetOnboardingCheck(
    clientOrganizationId,
    configurationId,
    userCheck,
    version,
  );

  return { onboardingChecked, refetchOnboardingCheck };
};

export const useTelemetryStat = (events: any[], dateFrom: string, dateTo: string, clientOrganizationId?: string) =>
  useQuery({
    queryKey: ['telemetryStat', dateFrom, dateTo, events, clientOrganizationId],
    queryFn: () => {
      return aamBackendApi.getTelemetryStat(dateFrom, dateTo, events, clientOrganizationId);
    },
  });

export const mutateAcknowledgeDocument = (
  tenantId: string,
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  callback?: any,
) =>
  useMutation({
    mutationFn: ({ document, additionalData }: { document: OOUserDocumentModel; additionalData: any }) =>
      aamBackendApi.acknowledgeDocument(
        tenantId as any,
        clientOrganizationId!,
        configurationId!,
        document.id,
        userId as any,
        additionalData,
      ),
    onSuccess: (data) => callback?.(data),
  });

export const mutateDocumentViewStatus = (tenantId: string, userId: string, callback?: any) =>
  useMutation({
    mutationFn: ({ document, additionalData }: { document: OOUserDocumentModel; additionalData: any }) =>
      aamBackendApi.updateDocumentViewStatus(tenantId as any, document.id, userId as any, additionalData),
    onSuccess: (data) => callback?.(data),
  });

export const useCheckGeneratedDocumentsStatus = (
  tenantId: string,
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  retryCount: number,
  journeyHasGeneratedDocuments: boolean,
  callback?: any,
  refetchInterval?: number | false,
) => {
  const query = useQuery({
    queryKey: ['checkGeneratedDocumentsStatus', userId, clientOrganizationId, configurationId],
    queryFn: () =>
      aamBackendApi.checkGeneratedDocumentsStatus(
        tenantId as any,
        clientOrganizationId!,
        configurationId!,
        userId as any,
        applicationId,
        retryCount,
      ),
    enabled: !!userId && journeyHasGeneratedDocuments,
    retry: 3,
    refetchInterval,
    refetchOnMount: journeyHasGeneratedDocuments,
  });
  const updatedAt = useRef(query.dataUpdatedAt);
  useEffect(() => {
    if (query.data && callback) {
      if (updatedAt.current !== query.dataUpdatedAt) {
        callback(query.data);
      }
    }
  }, [query.dataUpdatedAt]);

  return query;
};

export const useSoftDeletedApplications = (
  {
    filters,
    page,
    order,
    orderBy,
    onboardingDeletedAtInterval,
    rowsPerPage,
    tenantId,
    otherFilters,
    journeyId,
    searchOnboardingEndpointVersion,
  }: UseOnboardingFlowUsersOptions,
  callbacks?: (data: any) => void,
) => {
  const query = useQuery<SearchResult>({
    queryKey: [
      'getSoftDeletedApplications',
      filters,
      page,
      order,
      orderBy,
      onboardingDeletedAtInterval,
      rowsPerPage,
      tenantId,
      otherFilters,
      journeyId,
      searchOnboardingEndpointVersion,
    ],
    queryFn: () => {
      const payload = {
        page: page + 1,
        sortDir: order.toUpperCase(),
        sort: orderBy,
        ...otherFilters,
        ...onboardingDeletedAtInterval,
        isOO: true,
        clientOrganizationId: filters.clientOrganizationId,
        configurationId: journeyId,
        resultsPerPage: rowsPerPage,
      };

      return aamBackendApi.fetchSoftDeletedUsers(payload);
    },
  });

  useEffect(() => {
    if (query.data && callbacks) {
      callbacks(query.data);
    }
  }, [query.data, callbacks]);

  return query;
};

export const mutateIntegrationState = (
  clientOrganizationId: string,
  configurationId: string,
  selectedUserId: string,
  applicationId: string,
  callbacks?: (data: any) => void,
) =>
  useMutation({
    mutationFn: async (integrationState: Record<string, any>) => {
      try {
        return await aamBackendApi.updateIntegrationState(
          clientOrganizationId,
          configurationId,
          selectedUserId,
          applicationId,
          integrationState,
        );
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (data) => callbacks?.(data),
  });

export const mutateOnboardingCustomData = (
  clientOrganizationId: string,
  configurationId: string,
  selectedUserId: string,
  applicationId: string,
  callbacks?: (data: any) => void,
) =>
  useMutation({
    mutationFn: async (customData: Record<string, any>) => {
      try {
        return await aamBackendApi.updateOnboardingCustomData(
          clientOrganizationId,
          configurationId,
          selectedUserId,
          applicationId,
          customData,
        );
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (data) => callbacks?.(data),
  });

export const mutateUserFlowStepChange = (
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  callbacks?: (data: any) => void,
) =>
  useMutation({
    mutationFn: (payload: Record<string, any>) => {
      return aamBackendApi.updateUserFlowStep(
        clientOrganizationId!,
        configurationId!,
        userId!,
        payload,
        null,
        applicationId!,
      );
    },
    onSuccess: (data) => callbacks?.(data),
  });

export const useGetBulkMailListandUpdateStore = (bulkMailPayload: getBulkMailPayloadType) => {
  const {
    page = 1,
    resultPerPage = 10,
    sort = 'createdAt',
    sortDir = 'DESC',
    status,
    refetchOnMount = true,
    callback,
  } = bulkMailPayload;
  const query = useQuery({
    queryKey: ['getBulkMailList', page, resultPerPage, sort, sortDir, status],
    queryFn: () => aamBackendApi.fetchBulkEmailListForTenant(page, resultPerPage, sort, sortDir, status),
    refetchOnMount,
  });

  useEffect(() => {
    if (query.data && callback) {
      callback(query.data);
    }
  }, [query.data]);

  return query;
};

export const useGetClientOrganizationForTenantByTenant = () =>
  useQuery({
    queryFn: () => aamBackendApi.fetchClientOrganizationForTenantByTenant(),
    queryKey: ['fetchClientOrganizationForTenantByTenant'],
  });

export const mutateDcDocument = (
  clientOrganizationId: string,
  configurationId: string,
  userId: string,
  applicationId: string,
  queryClient: QueryClient,
  callback?: any,
) =>
  useMutation({
    mutationFn: async (payload: UploadBase64DocumentPayload) => {
      const {
        tenantId,
        type,
        name,
        clientOrganizationId,
        configurationId,
        userId,
        dcDocumentType,
        dcAdditionalDocumentType,
        sdkCapturedDocument,
      } = payload;
      try {
        return await aamBackendApi.postDcSdkCapturedDocument(
          tenantId,
          type,
          name,
          clientOrganizationId,
          configurationId,
          userId,
          sdkCapturedDocument,
          dcDocumentType,
          dcAdditionalDocumentType,
        );
      } catch (err) {
        throw err;
      }
    },
    onSuccess: () => {
      callback?.();
      queryClient.invalidateQueries({
        queryKey: ['getUserFlow', clientOrganizationId, configurationId, userId, applicationId],
      });
    },
  });

export const useFetchDCVerificationStatus = (
  userId: string,
  applicationId: string,
  clientOrganizationId: string,
  configurationId: string,
  onSuccess?: (data: any) => void,
) => {
  const query = useQuery({
    queryKey: ['fetchDCVerificationStatus', userId, applicationId, clientOrganizationId, configurationId],
    queryFn: () => aamBackendApi.getDcVerificationStatus(userId, applicationId, clientOrganizationId, configurationId),
  });
  useEffect(() => {
    if (query.data && onSuccess) {
      onSuccess(query.data);
    }
  }, [query.data]);

  return query;
};

export type TriggerCandidateProfileFetchPayload = {
  tenantId: number;
  candidateId: string;
  clientOrganizationId: string;
  applicationId: string;
  configurationId: string;
  email: string;
};

export const mutateTriggerCandidateProfileFetch = (callback?: (data: any) => void) =>
  useMutation({
    mutationFn: (payload: TriggerCandidateProfileFetchPayload) =>
      aamBackendApi.triggerCandidateProfileFetch(
        payload.tenantId,
        payload.candidateId,
        payload.clientOrganizationId,
        payload.applicationId,
        payload.configurationId,
        payload.email,
      ),
    onSuccess: (data) => callback?.(data),
  });

export const useGetSyncCandidateFromInfoStatus = (fetchInfoStatusPayload: any) => {
  const { clientOrganizationId, configurationId, userId, applicationId, callback } = fetchInfoStatusPayload;
  const enabled = !!(clientOrganizationId && configurationId && userId && applicationId);
  const query = useQuery({
    queryKey: ['getSyncCandidateFromInfoStatus', clientOrganizationId, configurationId, userId, applicationId],
    queryFn: () =>
      aamBackendApi.getSyncCandidateFromInfoStatus(clientOrganizationId, configurationId, userId, applicationId),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });
  const cbRef = useRef<((data: unknown) => void) | undefined>(undefined);
  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  const lastCalledAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!cbRef.current) return;
    if (!query.isSuccess) return;
    if (query.fetchStatus !== 'idle') return;
    if (lastCalledAtRef.current === query.dataUpdatedAt) return;
    lastCalledAtRef.current = query.dataUpdatedAt;
    cbRef.current(query.data);
  }, [query.isSuccess, query.fetchStatus, query.dataUpdatedAt, query.data]);

  return query;
};
